import {patchHostBridge} from "@frontend/hostContract"

/**
 * Live CaseInfo snapshots for host pull via `RepairIntelligence.getCaseInfo`.
 * Always reflects the latest known case facts (intake + diagnosis + parts).
 * Mid-funnel events stay lean; hosts pull here on demand.
 * `next_step_selected` still embeds `case` because handoff must be self-contained.
 */

const caseInfoById = new Map<string, CaseInfo>()
let activeCaseId: string | null = null

/**
 * Merge snapshots. Sparse updates omit fields; `buildCaseInfo` fills missing
 * scalars with `null` — don't let those wipe richer values already stored.
 */
const mergeCaseInfo = (previous: CaseInfo | undefined, next: CaseInfo): CaseInfo => {
  if (!previous) return {...next, id: next.id}

  const pickScalar = <T>(
    nextValue: T | null | undefined,
    previousValue: T | null | undefined,
  ): T | null | undefined => {
    if (nextValue != null) return nextValue
    if (previousValue != null) return previousValue
    return nextValue !== undefined ? nextValue : previousValue
  }

  return {
    id: next.id,
    brand: pickScalar(next.brand, previous.brand),
    modelNumber: pickScalar(next.modelNumber, previous.modelNumber),
    applianceType: pickScalar(next.applianceType, previous.applianceType),
    applianceAge: pickScalar(next.applianceAge, previous.applianceAge),
    applianceAgeMonths: pickScalar(
      next.applianceAgeMonths,
      previous.applianceAgeMonths,
    ),
    symptom: pickScalar(next.symptom, previous.symptom),
    causes: next.causes != null ? next.causes : previous.causes,
    parts: next.parts != null ? next.parts : previous.parts,
  }
}

/** Upsert the live snapshot for a case and mark it active. */
export const setHostCaseInfo = (caseInfo: CaseInfo): void => {
  const merged = mergeCaseInfo(caseInfoById.get(caseInfo.id), caseInfo)
  caseInfoById.set(caseInfo.id, merged)
  activeCaseId = caseInfo.id
}

/**
 * Current CaseInfo for `caseId`, or the active case when omitted.
 * Returns null when unknown / no session case yet.
 */
export const getHostCaseInfo = (caseId?: string): CaseInfo | null => {
  const id =
    typeof caseId === "string" && caseId.trim() !== ""
      ? caseId.trim()
      : activeCaseId
  if (!id) return null
  return caseInfoById.get(id) ?? null
}

export const clearHostCaseInfo = (): void => {
  caseInfoById.clear()
  activeCaseId = null
}

/** Test-only. */
export const resetHostCaseInfoForTests = (): void => {
  clearHostCaseInfo()
}

/** Assigns script-added `getCaseInfo` on the host bridge. */
export const registerHostCaseInfoApi = (): void => {
  patchHostBridge({getCaseInfo: getHostCaseInfo})
}
